import { forwardRef } from 'react'
import { CurrencyDollarIcon, SearchIcon } from '@heroicons/react/outline'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { useApp } from 'providers/app'
import { Label } from '../Typography'
import PropTypes from 'prop-types'

/**
 * Input
 *
 * Accepts various standard input props and an event callback
 * Icon set will be increased if needed
 */
const Input = forwardRef(
  (
    {
      value = '',
      type = 'text',
      autoCapitalize = 'none',
      autoComplete,
      error,
      center,
      required,
      name,
      label,
      placeholder,
      fullwidth,
      icon,
      loading,
      min,
      max,
      step,
      pattern,
      minlength,
      maxlength,
      onChange,
      onKeyPress,
      prompt,
    },
    ref
  ) => {
    // Access theme
    const { primaryTextStyle } = useApp()

    // Local state
    let labelOutput,
      promptOutput,
      iconOutput,
      classes = `rounded py-3 w-full ${
        primaryTextStyle ? '' : 'focus:ring-purple-600 focus:border-purple-600'
      } `

    /**
     * On input change
     */
    const handleChange = (e) => {
      let error
      let { value, validationMessage } = e.target

      // Sanitize & validate input based on type
      switch (type) {
        case 'tel': {
          const phone = parsePhoneNumberFromString(value, 'US')
          const isValid = phone ? phone.isValid() : false
          value = phone ? phone.formatNational() : value
          error = !isValid
          break
        }
        default: {
          error = validationMessage
          break
        }
      }
      // Pass back value
      onChange(value, name, error)
    }

    // Fullwidth check
    if (fullwidth) classes += 'w-full '

    // Center check
    if (center) classes += 'text-center '

    // Error check
    classes += error ? 'border-red-500 ' : ' border-gray-200 '

    // Input output
    if (icon) classes += 'pl-12 '
    switch (icon) {
      case 'search':
        iconOutput = <SearchIcon />
        break
      case 'currency':
        iconOutput = <CurrencyDollarIcon />
        break
    }

    // Render component
    return (
      <div>
        {/* Label */}
        {(label || prompt) && (
          <div className="mb-2 flex items-center justify-between">
            <div>{label && <Label small>{label}</Label>}</div>
            <div>
              {prompt && (
                <span className="block text-right text-sm text-gray-500">
                  {prompt}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="relative">
          {/* Icon */}
          {icon && (
            <div
              className="pointer-events-none absolute left-0 top-0 flex h-12 w-12 items-center justify-center"
              style={{ top: 1 }}
            >
              <div className="block h-5 w-5 text-gray-600">{iconOutput}</div>
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            type={type}
            name={name}
            value={value}
            required={required}
            autoCapitalize={autoCapitalize}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyPress={onKeyPress}
            className={classes}
            min={min}
            max={max}
            step={step}
            pattern={pattern}
            minLength={minlength}
            maxLength={maxlength}
          />

          {/* Loading */}
          {loading && (
            <div className="absolute right-0 top-0 p-4">
              <svg
                className="h-5 w-5 animate-spin text-purple-500"
                style={primaryTextStyle}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
        </div>
      </div>
    )
  }
)

// Display name
Input.displayName = 'Input'

// Define prop types
Input.propTypes = {
  value: PropTypes.string, // Is sometimes an object
  type: PropTypes.string,
  autoCapitalize: PropTypes.string,
  autoComplete: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  center: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string, // Sometimes a bool
  prompt: PropTypes.node,
  placeholder: PropTypes.string,
  fullwidth: PropTypes.bool,
  icon: PropTypes.string,
  loading: PropTypes.bool,
  min: PropTypes.string,
  max: PropTypes.string,
  step: PropTypes.string,
  pattern: PropTypes.string,
  minlength: PropTypes.string,
  maxlength: PropTypes.string,
  onChange: PropTypes.func,
  onKeyPress: PropTypes.func,
}

export default Input
